import { motion } from "framer-motion";
import { ArrowDown, ArrowRight, Music } from "lucide-react";
import { openMission } from "./Mission";
import { trackEvent } from "@/lib/analytics";

const HERO_BG = "https://images.pexels.com/photos/9252028/pexels-photo-9252028.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=1920";

export const Hero = () => (
  <section id="top" data-testid="hero-section" className="relative min-h-screen flex items-center overflow-hidden">
    <div className="absolute inset-0">
      <img src={HERO_BG} alt="Light breaking through dark clouds" className="h-full w-full object-cover animate-slow-zoom" />
      <div className="absolute inset-0 bg-[#0A0A0A]/75" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-[#0A0A0A]/60" />
    </div>

    <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10 w-full pt-28 pb-16">
      <motion.p
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7 }}
        className="text-xs font-semibold tracking-[0.3em] uppercase text-[#D4AF37] mb-6"
      >
        The Original Gospel of Christ · Restored
      </motion.p>

      <motion.h1
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
        className="font-heading font-light tracking-tighter leading-[0.92] text-5xl md:text-7xl lg:text-8xl max-w-5xl text-[#F4F0EA]"
      >
        We are putting back in
        <br />
        <span className="italic text-[#D4AF37]">what they took out.</span>
      </motion.h1>

      <motion.p
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, delay: 0.25 }}
        className="mt-8 max-w-2xl text-base md:text-lg leading-relaxed font-light text-[#A39E98]"
      >
        Restoring the true teachings of Christ — the dietary law, the holy
        commandments, the Sabbath, and His appointed Feast Days. This is not
        tradition. This is obedience.
      </motion.p>

      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, delay: 0.4 }}
        className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-4"
      >
        <a
          href="#teachings"
          data-testid="hero-cta-primary"
          className="bg-[#D4AF37] text-[#0A0A0A] font-medium px-8 py-4 hover:bg-[#E5C865] transition-colors"
        >
          Discover the Teachings
        </a>
        <a
          href="#join"
          data-testid="hero-cta-secondary"
          className="border border-[#4A453F] text-[#F4F0EA] px-8 py-4 hover:border-[#D4AF37] transition-colors"
        >
          Stand With Us
        </a>
        <button
          onClick={openMission}
          data-testid="hero-mission-button"
          className="border border-[#4A453F] text-[#F4F0EA] px-8 py-4 hover:border-[#D4AF37] transition-colors"
        >
          Our Mission
        </button>
      </motion.div>

      <motion.a
        href="https://www.tvbeyth-el.net"
        target="_blank"
        rel="noopener noreferrer"
        data-testid="hero-learn-more"
        onClick={() => trackEvent("click_learn_more", { location: "hero", link_url: "https://www.tvbeyth-el.net" })}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.9, delay: 0.55 }}
        className="mt-8 inline-flex items-center gap-2 text-sm tracking-wide text-[#D4AF37] hover:text-[#E5C865] transition-colors group"
      >
        To Learn more, click here.
        <ArrowRight strokeWidth={1.5} className="h-4 w-4 transition-transform group-hover:translate-x-1" />
      </motion.a>

      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 0.7 }}
        className="mt-16 font-heading italic text-2xl md:text-3xl font-light text-[#F4F0EA]/90"
      >
        Salvation is on the line. The Kingdom of Heaven is on the line.
      </motion.p>

      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, delay: 0.85 }}
        data-testid="hero-music-player"
        className="mt-8 max-w-md border border-[#2B2825] bg-[#0A0A0A]/60 backdrop-blur-md p-4"
      >
        <div className="flex items-center gap-2 mb-3">
          <Music strokeWidth={1.5} className="h-4 w-4 text-[#D4AF37]" />
          <p className="font-mono text-xs tracking-[0.2em] uppercase text-[#D4AF37]">
            Now Playing — Holy is the Lamb
          </p>
        </div>
        <audio
          data-testid="hero-audio"
          className="w-full"
          src="https://customer-assets.emergentagent.com/job_sabbath-way/artifacts/1ia3gfb4_Holy%20is%20the%20Lamb.mp3"
          controls
          preload="metadata"
        />
      </motion.div>
    </div>

    <a href="#teachings" className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 text-[#A39E98] hover:text-[#D4AF37] transition-colors">
      <ArrowDown strokeWidth={1} className="animate-bounce" />
    </a>
  </section>
);
